import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  CheckCircle2, 
  AlertCircle,
  Loader2 
} from 'lucide-react';

/**
 * Get demand level color and icon
 */
const getDemandIndicator = (score) => {
  if (score >= 80) {
    return { color: 'bg-red-500', label: 'Peak', icon: '🔴' };
  } else if (score >= 65) {
    return { color: 'bg-orange-500', label: 'High', icon: '🟠' };
  } else if (score >= 45) {
    return { color: 'bg-yellow-500', label: 'Medium', icon: '🟡' };
  } else {
    return { color: 'bg-green-500', label: 'Low', icon: '🟢' };
  }
};

/**
 * Get status badge variant
 */
const getStatusBadge = (status) => {
  if (status === 'following_ai') {
    return (
      <Badge variant="success" className="gap-1">
        <CheckCircle2 className="h-3 w-3" />
        AI
      </Badge>
    );
  } else {
    return (
      <Badge variant="warning" className="gap-1">
        <AlertCircle className="h-3 w-3" />
        Manual
      </Badge>
    );
  }
};

/**
 * Format currency
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Format date
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Pricing Table Row Component
 */
const PricingTableRow = ({ recommendation, onApply, loading }) => {
  const [applying, setApplying] = useState(false);
  const demand = getDemandIndicator(recommendation.demand_score);
  const needsUpdate = recommendation.status !== 'following_ai';

  const handleApply = async () => {
    setApplying(true);
    try {
      await onApply(recommendation);
    } finally {
      setApplying(false);
    }
  };

  return (
    <TableRow className={needsUpdate ? 'bg-yellow-50/50 dark:bg-yellow-950/10' : ''}>
      {/* Date */}
      <TableCell className="font-medium">
        <div className="flex flex-col">
          <span>{formatDate(recommendation.date)}</span>
          <span className="text-xs text-muted-foreground">
            {recommendation.day_of_week}
          </span>
        </div>
      </TableCell>

      {/* Demand */}
      <TableCell>
        <div className="flex items-center gap-2">
          <span className="text-2xl">{demand.icon}</span>
          <div className="flex flex-col">
            <span className="font-medium">{Math.round(recommendation.demand_score)}</span>
            <span className="text-xs text-muted-foreground">{demand.label}</span>
          </div>
        </div>
      </TableCell>

      {/* Competitor Avg */}
      <TableCell className="text-right">
        {formatCurrency(recommendation.competitor_avg)}
      </TableCell>

      {/* Occupancy */}
      <TableCell className="text-right">
        <Badge variant="outline">
          {Math.round(recommendation.occupancy_percent)}%
        </Badge>
      </TableCell>

      {/* Current Price */}
      <TableCell className="text-right font-medium">
        {formatCurrency(recommendation.current_price)}
      </TableCell>

      {/* AI Recommended */}
      <TableCell className="text-right">
        <div className="flex flex-col items-end">
          <span className="font-bold text-primary">
            {formatCurrency(recommendation.recommended_price)}
          </span>
          <span className="text-xs text-muted-foreground">
            Conf: {(recommendation.confidence_score * 100).toFixed(0)}%
          </span>
        </div>
      </TableCell>

      {/* Difference */}
      <TableCell className="text-right">
        {recommendation.difference !== 0 ? (
          <div className="flex items-center justify-end gap-1">
            {recommendation.difference > 0 ? (
              <TrendingUp className="h-4 w-4 text-green-600" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-600" />
            )}
            <span className={recommendation.difference > 0 ? 'text-green-600' : 'text-red-600'}>
              {recommendation.difference > 0 ? '+' : ''}
              {formatCurrency(Math.abs(recommendation.difference))}
            </span>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1 text-muted-foreground">
            <Minus className="h-4 w-4" />
            <span>--</span>
          </div>
        )}
      </TableCell>

      {/* Status */}
      <TableCell>
        {getStatusBadge(recommendation.status)}
      </TableCell>

      {/* Actions */}
      <TableCell>
        {needsUpdate && (
          <Button
            size="sm"
            onClick={handleApply}
            disabled={applying || loading}
          >
            {applying ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Apply'
            )}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

/**
 * Main Pricing Table Component
 */
const PricingTable = ({ recommendations, onApply, loading }) => {
  if (!recommendations || recommendations.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pricing Recommendations</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <div className="max-h-[600px] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Demand</TableHead>
                  <TableHead className="text-right">Comp Avg</TableHead>
                  <TableHead className="text-right">Occ %</TableHead>
                  <TableHead className="text-right">Current</TableHead>
                  <TableHead className="text-right">AI Rec</TableHead>
                  <TableHead className="text-right">Diff</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recommendations.map((rec) => (
                  <PricingTableRow
                    key={rec.date}
                    recommendation={rec}
                    onApply={onApply}
                    loading={loading}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>🔴 Peak (80+)</span>
            <span>🟠 High (65-79)</span>
            <span>🟡 Medium (45-64)</span>
            <span>🟢 Low (&lt;45)</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="success" className="gap-1">
              <CheckCircle2 className="h-3 w-3" />
              AI
            </Badge>
            <span>= Following AI recommendation</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="warning" className="gap-1">
              <AlertCircle className="h-3 w-3" />
              Manual
            </Badge>
            <span>= Manual override</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PricingTable;
